import { useCallback, useMemo, useState, ReactNode } from "react";
import { NotificationContext } from "./NotificationContext";
import { Toast, ToastToAdd, ToastToRemove } from "./types";
import uniqueId from "lodash-es/uniqueId";

const generateToastId = () => uniqueId("toast_");

type NotificationProviderProps = {
  children?: ReactNode;
  /** List of toasts to show on render. */
  initialToasts?: Toast[];
};

export const NotificationProvider = ({
  children,
  initialToasts = [],
}: NotificationProviderProps) => {
  const [activeToasts, setActiveToasts] = useState<Toast[]>(initialToasts);

  // both only use the functional form of setActiveToasts, so they never need to
  // be recreated. Consumers rely on that: a self-closing toast lists removeToasts
  // as an effect dependency, and an unstable one would restart its timer
  const addToasts = useCallback((toastsToAdd: ToastToAdd[]) => {
    setActiveToasts((prevToasts) => {
      const toastsWithIds = toastsToAdd.map((toast) => ({
        ...toast,
        id: generateToastId(),
      }));
      return [...prevToasts, ...toastsWithIds];
    });
  }, []);

  const removeToasts = useCallback((toastsToRemove: ToastToRemove[]) => {
    setActiveToasts((prevToasts) => {
      const toastsToRemoveIds = toastsToRemove.map((toast) => toast.id);
      const remainingToasts = prevToasts.filter((toast) => {
        return !toastsToRemoveIds.includes(toast.id);
      });
      return remainingToasts;
    });
  }, []);

  const value = useMemo(
    () => ({ activeToasts, addToasts, removeToasts }),
    [activeToasts, addToasts, removeToasts],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
